import {
    asContentJsonObject
} from "../../services/content/contentRepository.js";

import type {
    AdminContentItemRpcRow,
    AdminContentRevisionRpcRow,
    CanonicalContentType,
    Json
} from "../../services/backend/database.types.js";

import type {
    AdminContentDraft
} from "./adminContentRepository.js";


/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface AdminContentEditorValue {
    contentKey: string;

    contentType:
        CanonicalContentType;

    level:
        string;

    payloadText:
        string;

    schemaVersion:
        string;

    sourcePath:
        string;

    titleFa:
        string;

    titleFr:
        string;
}


/* -------------------------------------------------------------------------- */
/* Empty editor                                                               */
/* -------------------------------------------------------------------------- */

function createEmptyAdminContentEditor():
AdminContentEditorValue {
    const value:
        AdminContentEditorValue = {
            contentKey:
                "",

            contentType:
                "grammar_lesson",

            level:
                "A1",

            payloadText:
                "",

            schemaVersion:
                "2",

            sourcePath:
                "admin-panel",

            titleFa:
                "",

            titleFr:
                ""
        };

    return {
        ...value,

        payloadText:
            createAdminPayloadTemplate(
                value
            )
    };
}


/* -------------------------------------------------------------------------- */
/* Existing revision                                                          */
/* -------------------------------------------------------------------------- */

function createAdminContentEditorFromRevision(
    item:
        AdminContentItemRpcRow,

    revision:
        AdminContentRevisionRpcRow
): AdminContentEditorValue {
    return {
        contentKey:
            item.content_key,

        contentType:
            item.content_type,

        level:
            revision.level
            ?? "",

        payloadText:
            JSON.stringify(
                revision.payload,
                null,
                2
            ),

        schemaVersion:
            String(
                revision.schema_version
            ),

        sourcePath:
            revision.source_path
            ?? "admin-panel",

        titleFa:
            revision.title_fa
            ?? "",

        titleFr:
            revision.title_fr
    };
}


/* -------------------------------------------------------------------------- */
/* Payload template                                                           */
/* -------------------------------------------------------------------------- */

function createAdminPayloadTemplate(
    value:
        AdminContentEditorValue
): string {
    const id =
        value.contentKey.trim();

    const title =
        value.titleFr.trim();

    const level =
        value.level.trim();


    /* ---------------------------------------------------------------------- */
    /* Home feature                                                           */
    /* ---------------------------------------------------------------------- */

    if (
        value.contentType
        === "home_feature"
    ) {
        const shared:
            Record<
                string,
                Json | undefined
            > = {
                id,

                kind:
                    "daily_phrase",

                title,

                title_fa:
                    value.titleFa.trim()
                    || undefined,

                label:
                    "PHRASE DU JOUR",

                label_fa:
                    undefined,

                meta:
                    undefined,

                meta_fa:
                    undefined,

                icon:
                    "💬",

                href:
                    undefined,

                position:
                    10,

                featured:
                    false,

                startsOn:
                    undefined,

                endsOn:
                    undefined
            };

        return JSON.stringify(
            {
                catalog:
                    compactJsonObject(
                        shared
                    ),

                document:
                    compactJsonObject(
                        shared
                    )
            },
            null,
            2
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Generic learning content                                               */
    /* ---------------------------------------------------------------------- */

    const catalog:
        Record<
            string,
            Json
        > = {
            id,
            title
        };

    const document:
        Record<
            string,
            Json
        > = {
            id,
            sections: [],
            title
        };


    if (
        level
    ) {
        catalog.level =
            level;

        document.level =
            level;
    }


    /* ---------------------------------------------------------------------- */
    /* Grammar                                                                */
    /* ---------------------------------------------------------------------- */

    if (
        value.contentType
        === "grammar_lesson"
    ) {
        Object.assign(
            catalog,
            {
                estimatedTime:
                    10,

                exercises:
                    0,

                category:
                    "base",

                icon:
                    "📘",

                importance:
                    1,

                lessons:
                    1,

                module:
                    "Nouveau module",

                prerequisites:
                    [],

                recommended:
                    false
            }
        );

        Object.assign(
            document,
            {
                estimatedTime:
                    10,

                icon:
                    "📘"
            }
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Vocabulary                                                             */
    /* ---------------------------------------------------------------------- */

    if (
        value.contentType
        === "vocabulary_pack"
    ) {
        Object.assign(
            catalog,
            {
                icon:
                    "📚",

                title_fa:
                    value.titleFa.trim()
                    || undefined,

                words:
                    1
            }
        );

        delete document.sections;

        Object.assign(
            document,
            {
                icon:
                    "📚",

                title_fa:
                    value.titleFa.trim()
                    || undefined,

                words: [
                    {
                        difficulty:
                            1,

                        emoji:
                            "🦕",

                        fa:
                            "",

                        fr:
                            ""
                    }
                ],

                quiz: {
                    displayCount:
                        1,

                    questions:
                        []
                }
            }
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Travel                                                                 */
    /* ---------------------------------------------------------------------- */

    if (
        value.contentType
        === "travel_lesson"
    ) {
        Object.assign(
            catalog,
            {
                icon:
                    "🧳",

                title_fa:
                    value.titleFa.trim()
                    || undefined,

                estimatedTime:
                    10
            }
        );

        delete document.sections;

        Object.assign(
            document,
            {
                path:
                    "travel",

                icon:
                    "🧳",

                title_fa:
                    value.titleFa.trim()
                    || undefined,

                estimatedTime:
                    10,

                miniLessons:
                    []
            }
        );
    }


    /* ---------------------------------------------------------------------- */
    /* Result                                                                 */
    /* ---------------------------------------------------------------------- */

    return JSON.stringify(
        {
            catalog,

            document,

            ...(
                value.contentType
                === "grammar_lesson"
                    ? {
                        exerciseSections:
                            []
                    }
                    : {}
            )
        },
        null,
        2
    );
}


/* -------------------------------------------------------------------------- */
/* Parse editor                                                               */
/* -------------------------------------------------------------------------- */

function parseAdminContentEditor(
    value:
        AdminContentEditorValue
): AdminContentDraft {
    let parsed:
        Json;

    try {
        parsed =
            JSON.parse(
                value.payloadText
            ) as Json;
    } catch (error) {
        throw new TypeError(
            "Le payload JSON n’est pas valide.",
            {
                cause:
                    error
            }
        );
    }

    asContentJsonObject(
        parsed,
        "admin payload"
    );

    return {
        contentKey:
            value.contentKey.trim(),

        contentType:
            value.contentType,

        level:
            value.level.trim()
            || null,

        payload:
            parsed,

        schemaVersion:
            Number(
                value.schemaVersion
            ),

        sourcePath:
            value.sourcePath.trim()
            || null,

        titleFa:
            value.titleFa.trim()
            || null,

        titleFr:
            value.titleFr.trim()
    };
}


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * JSON.stringify ignores undefined properties anyway, but compacting the
 * object here keeps the generated Home payload explicit and predictable.
 */
function compactJsonObject(
    value:
        Record<
            string,
            Json | undefined
        >
): Record<
    string,
    Json
> {
    return Object.fromEntries(
        Object.entries(
            value
        ).filter(
            ([, item]) =>
                item !== undefined
        )
    ) as Record<
        string,
        Json
    >;
}


/* -------------------------------------------------------------------------- */
/* Exports                                                                    */
/* -------------------------------------------------------------------------- */

export {
    createAdminContentEditorFromRevision,
    createAdminPayloadTemplate,
    createEmptyAdminContentEditor,
    parseAdminContentEditor
};

export type {
    AdminContentEditorValue
};